import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { catchError, mergeMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { AccountActions } from './account.actions';
import {
  DocumentData,
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
} from 'firebase/firestore';
import { FIRESTORE } from 'src/app/common/firebase/firebase.tokens';
import { Agency, CreditAccount, UserType } from '@enrollhere/classes';
import { Action } from '@ngrx/store';
import { getUserCollection } from '../../../misc/getUserCollection';
import { CreditAccountService } from '../../../services/credit-account.service';

/**
 * @deprecated NgRx effects with direct Firestore access are deprecated. Use API endpoints instead.
 */
@Injectable()
export class AccountEffects {
  private actions$ = inject(Actions);
  private creditAccountService = inject(CreditAccountService);
  /** @deprecated Direct Firestore access is deprecated. Use API endpoints instead. */
  private firestore = inject(FIRESTORE);

  private resolveUserCollection(userType?: UserType) {
    return userType ? (getUserCollection(userType) ?? 'agencies') : 'agencies';
  }

  private mapAccountFromDoc(
    docSnapshot: { id: string; data: () => Record<string, unknown> },
    agencyId: string
  ) {
    const agency: Agency | undefined = undefined;
    return CreditAccount.fromJSON({
      ...docSnapshot.data(),
      id: docSnapshot.id,
      owner: {
        id: agencyId,
        name: (agency as unknown as Agency)?.name,
        type: UserType.AGENCY,
      },
    });
  }

  private emitLoadAccountsSuccess(
    subscriber: { next: (action: Action) => void },
    snapshot: {
      docs: Array<{ id: string; data: () => Record<string, unknown> }>;
    },
    agencyId: string
  ) {
    const accounts = snapshot.docs.map(docSnapshot =>
      this.mapAccountFromDoc(docSnapshot, agencyId)
    );
    console.info('accounts', accounts);
    subscriber.next(AccountActions.loadAccountsSuccess({ accounts, agencyId }));
  }

  private emitLoadAccountsFailure(
    subscriber: { next: (action: Action) => void },
    error: unknown
  ) {
    console.log('loadAccounts$ error', error);
    subscriber.next(AccountActions.loadAccountsFailure({ error }));
  }

  private createAddAccountSuccess(
    account: CreditAccount,
    agencyId: string,
    id: string
  ) {
    return AccountActions.addAccountSuccess({
      account: CreditAccount.fromJSON({ ...account, id }),
      agencyId,
    });
  }

  private createRemoveAccountSuccess(accountId: string, agencyId: string) {
    return AccountActions.removeAccountSuccess({ accountId, agencyId });
  }

  private createUpdateAccountSuccess(
    agencyId: string,
    accountId: string,
    account: Partial<CreditAccount>
  ) {
    return AccountActions.updateAccountSuccess({
      agencyId,
      accountId,
      account,
    });
  }

  loadAccounts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountActions.loadAccounts),
      mergeMap(({ agencyId, userType }) => {
        console.info('loadAccounts$', { agencyId, userType });

        const userCollection = this.resolveUserCollection(userType);

        return new Observable<Action>(subscriber => {
          const unsubscribe = onSnapshot(
            collection(
              this.firestore,
              userCollection,
              agencyId,
              'creditAccounts'
            ),
            async snapshot => {
              this.emitLoadAccountsSuccess(
                subscriber,
                snapshot as {
                  docs: Array<{
                    id: string;
                    data: () => Record<string, unknown>;
                  }>;
                },
                agencyId
              );
            },
            error => {
              this.emitLoadAccountsFailure(subscriber, error);
            }
          );

          return unsubscribe;
        }).pipe(
          catchError(error => {
            console.log('loadAccounts$ error', error);
            return of({ type: '[Account API] Load Accounts Error', error });
          })
        );
      })
    );
  });

  loadAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountActions.loadAccount),
      mergeMap(
        ({ agencyId, accountId, userType }) =>
          new Observable<Action>(subscriber => {
            console.log('loadAccount$', { agencyId, accountId, userType });

            this.creditAccountService
              .getAccount({
                id: accountId,
                owner: {
                  id: agencyId,
                  type: userType,
                },
              })
              .subscribe({
                next: res => {
                  console.log('loadAccount$ success', res);
                  if (!res?.data) {
                    subscriber.next(
                      AccountActions.loadAccountFailure({
                        error: 'Account not found',
                      })
                    );
                    return;
                  }
                  const account = CreditAccount.fromJSON({
                    ...res.data,
                    id: res.data.id,
                    owner: {
                      id: agencyId,
                      type: userType,
                    },
                  });
                  subscriber.next(
                    AccountActions.loadAccountSuccess({ account, agencyId })
                  );
                },
                error: error => {
                  console.log('loadAccount$ error', error);
                  subscriber.next(AccountActions.loadAccountFailure({ error }));
                },
              });
          })
      )
    );
  });

  addAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountActions.addAccount),
      mergeMap(async ({ account, agencyId, userType }) => {
        try {
          const userCollection = this.resolveUserCollection(userType);

          const accountDoc = CreditAccount.toJSON(account);

          const docRef = await addDoc(
            collection(
              this.firestore,
              userCollection,
              agencyId,
              'creditAccounts'
            ),
            accountDoc
          );
          return this.createAddAccountSuccess(account, agencyId, docRef.id); // return new account with id
        } catch (error) {
          return AccountActions.addAccountFailure({ error });
        }
      })
    );
  });

  removeAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountActions.removeAccount),
      mergeMap(async ({ agencyId, accountId, userType }) => {
        try {
          const userCollection = this.resolveUserCollection(userType);

          await deleteDoc(
            doc(
              this.firestore,
              userCollection,
              agencyId,
              'creditAccounts',
              accountId
            )
          );
          return this.createRemoveAccountSuccess(accountId, agencyId); // return removed account's id
        } catch (error) {
          return AccountActions.removeAccountFailure({ error });
        }
      })
    );
  });

  updateAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AccountActions.updateAccount),
      mergeMap(async ({ agencyId, accountId, account, userType }) => {
        try {
          console.log('updateAccount$', { agencyId, accountId, account });
          const userCollection = this.resolveUserCollection(userType);

          const accountDoc = CreditAccount.toJSON(account);

          await setDoc(
            doc(
              this.firestore,
              userCollection,
              agencyId,
              'creditAccounts',
              accountId
            ),
            accountDoc as DocumentData,
            {
              merge: true,
            }
          );
          return this.createUpdateAccountSuccess(agencyId, accountId, account); // return updated account's id and changes
        } catch (error) {
          console.log('updateAccount$ error', error);
          return AccountActions.updateAccountFailure({ error });
        }
      })
    );
  });
}
